import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { api, type FicheProjet, type FilProjet, lire } from '../api'
import { formaterInstant } from './Historique'
import { Icone } from './Icone'
import { Vide } from './Section'

// Les fils de commentaires d'un projet (onglet Commentaires) : sur le projet, sur une page, sur
// un passage d'une page (« Voir le passage » ouvre la page dessus). Un fil se résout (son
// passage n'est plus surligné) et se rouvre en y répondant.

export function FilsProjet({
  projet,
  pageId,
  filActif,
  onVoirPassage,
}: {
  projet: FicheProjet
  /** Page ouverte en dernier : proposée pour un nouveau fil. */
  pageId: string
  filActif: string | null
  onVoirPassage: (filId: string) => void
}) {
  const client = useQueryClient()
  const [voirResolus, setVoirResolus] = useState(false)
  const fils = projet.fils
  const ouverts = fils.filter((f) => !f.resoluLe)
  const resolus = fils.filter((f) => f.resoluLe)
  const rafraichir = () => client.invalidateQueries({ queryKey: ['projets', 'fiche', projet.id] })
  return (
    <div className="panneau-fils">
      {projet.ecriture && (
        <NouveauFil
          pages={projet.pages}
          pageId={pageId}
          onEnvoyer={async (texte, surPage) => {
            await lire(
              api.projets[':id'].fils.$post({
                param: { id: projet.id },
                json: surPage ? { texte, pageId: surPage } : { texte },
              }),
            )
            await rafraichir()
          }}
        />
      )}
      {ouverts.length === 0 && <Vide>Aucun fil ouvert.</Vide>}
      {ouverts.map((f) => (
        <Fil
          key={f.id}
          f={f}
          actif={f.id === filActif}
          ecriture={projet.ecriture}
          apres={rafraichir}
          onVoirPassage={onVoirPassage}
        />
      ))}
      {resolus.length > 0 && (
        <button type="button" className="bouton-texte" onClick={() => setVoirResolus(!voirResolus)}>
          {voirResolus ? 'Masquer' : 'Voir'} {resolus.length} fil{resolus.length > 1 ? 's' : ''}{' '}
          résolu{resolus.length > 1 ? 's' : ''}
        </button>
      )}
      {voirResolus &&
        resolus.map((f) => (
          <Fil
            key={f.id}
            f={f}
            actif={f.id === filActif}
            ecriture={projet.ecriture}
            apres={rafraichir}
            onVoirPassage={onVoirPassage}
          />
        ))}
    </div>
  )
}

function NouveauFil({
  pages,
  pageId,
  onEnvoyer,
}: {
  pages: FicheProjet['pages']
  pageId: string
  /** `surPage` : la page visée ; vide, tout le projet. */
  onEnvoyer: (texte: string, surPage: string) => Promise<void>
}) {
  const [texte, setTexte] = useState('')
  const [surPage, setSurPage] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)
  return (
    <form
      className="nouveau-fil"
      onSubmit={async (e) => {
        e.preventDefault()
        if (!texte.trim()) return
        setErreur(null)
        try {
          await onEnvoyer(texte.trim(), surPage)
          setTexte('')
        } catch (x) {
          setErreur((x as Error).message)
        }
      }}
    >
      <textarea
        value={texte}
        onChange={(e) => setTexte(e.target.value)}
        rows={2}
        placeholder="Nouveau commentaire… (sur un passage : le sélectionner dans sa page)"
        aria-label="Nouveau commentaire"
      />
      <div className="ligne-actions">
        <label className="filtre-pour">
          <span className="discret">Sur</span>
          <select
            value={surPage}
            onChange={(e) => setSurPage(e.target.value)}
            aria-label="Sur quoi porte le commentaire"
          >
            <option value="">Tout le projet</option>
            {pages.map((x) => (
              <option key={x.id} value={x.id}>
                {'· '.repeat(x.profondeur)}
                {x.titre}
                {x.id === pageId ? ' (ouverte)' : ''}
              </option>
            ))}
          </select>
        </label>
        <button type="submit" className="bouton petit" disabled={!texte.trim()}>
          Commenter
        </button>
      </div>
      {erreur && <p className="alerte">{erreur}</p>}
    </form>
  )
}

function Fil({
  f,
  actif,
  ecriture,
  apres,
  onVoirPassage,
}: {
  f: FilProjet
  actif: boolean
  ecriture: boolean
  apres: () => Promise<unknown>
  onVoirPassage: (filId: string) => void
}) {
  const [reponse, setReponse] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)
  const ref = useRef<HTMLElement>(null)
  useEffect(() => {
    if (actif) ref.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [actif])
  const agir = async (fn: () => Promise<unknown>) => {
    setErreur(null)
    try {
      await fn()
      await apres()
    } catch (e) {
      setErreur((e as Error).message)
    }
  }
  return (
    <article
      ref={ref}
      className={`fil-commentaires${actif ? ' actif' : ''}${f.resoluLe ? ' resolu' : ''}`}
      aria-label={`Fil de ${f.auteur}`}
    >
      <div className="filc-entete">
        <span className="discret">
          {f.pageId === null
            ? 'Sur le projet'
            : f.extrait
              ? `Sur un passage de « ${f.page} »`
              : `Sur « ${f.page} »`}
        </span>
        {ecriture && (
          <button
            type="button"
            className="bouton-texte"
            onClick={() =>
              agir(() =>
                lire(
                  api.projets.fils[':id'].resolution.$post({
                    param: { id: f.id },
                    json: { resolu: !f.resoluLe },
                  }),
                ),
              )
            }
          >
            <Icone nom={f.resoluLe ? 'restart_alt' : 'check'} />
            {f.resoluLe ? 'Rouvrir' : 'Résoudre'}
          </button>
        )}
      </div>
      {f.extrait && (
        <button
          type="button"
          className="filc-extrait"
          title={f.resoluLe ? undefined : 'Voir le passage dans sa page'}
          disabled={Boolean(f.resoluLe)}
          onClick={() => onVoirPassage(f.id)}
        >
          « {f.extrait} »
        </button>
      )}
      {f.commentaires.map((c) => (
        <div key={c.id} className="filc-message">
          <div className="discret">
            <strong>{c.auteur}</strong> · {formaterInstant(c.creeLe)}
          </div>
          <p>{c.texte}</p>
        </div>
      ))}
      {f.resoluLe && (
        <p className="discret">
          Résolu par {f.resoluPar ?? 'quelqu’un'} · {formaterInstant(f.resoluLe)}
        </p>
      )}
      {ecriture && (
        <form
          className="filc-reponse"
          onSubmit={(e) => {
            e.preventDefault()
            if (!reponse.trim()) return
            void agir(async () => {
              await lire(
                api.projets.fils[':id'].reponses.$post({
                  param: { id: f.id },
                  json: { texte: reponse.trim() },
                }),
              )
              setReponse('')
            })
          }}
        >
          <input
            type="text"
            value={reponse}
            onChange={(e) => setReponse(e.target.value)}
            placeholder={f.resoluLe ? 'Répondre (rouvre le fil)…' : 'Répondre…'}
            aria-label="Répondre au fil"
          />
          <button
            type="submit"
            className="bouton-icone"
            aria-label="Envoyer"
            disabled={!reponse.trim()}
          >
            <Icone nom="send" />
          </button>
        </form>
      )}
      {erreur && <p className="alerte">{erreur}</p>}
    </article>
  )
}
